import { Suspense } from "react"

import { Link, linkOptions, useParams, useSearch } from "@tanstack/react-router"

import { useSignupLink } from "@/components/account/useSignupLink"

/**
 * The cross-link between the two entry points, shown in the mobile header.
 * On desktop the same choice is offered in the body copy under each heading,
 * which is why this only ever renders on small screens.
 */
export function AccountHeaderLink({
  target,
  className,
  onClick,
}: {
  target: "login" | "signup"
  className?: string
  onClick?: () => void
}) {
  const { slug } = useParams({ strict: false })
  const { redirect } = useSearch({ strict: false })

  if (target === "login") {
    return (
      <Link
        className={className}
        onClick={onClick}
        search={{ redirect }}
        {...(slug
          ? linkOptions({ to: "/p/$slug/login", params: { slug } })
          : linkOptions({ to: "/login" }))}
      >
        Log in
      </Link>
    )
  }

  // Signup can be switched off, and that flag comes from a suspending query.
  // Keep the boundary here so a slow config fetch stalls the link rather than
  // the whole account layout.
  return (
    <Suspense fallback={null}>
      <SignupHeaderLink
        className={className}
        onClick={onClick}
        slug={slug}
        redirect={redirect}
      />
    </Suspense>
  )
}

function SignupHeaderLink({
  className,
  onClick,
  slug,
  redirect,
}: {
  className?: string
  onClick?: () => void
  slug?: string
  redirect?: string
}) {
  const toSignupOptions = useSignupLink(slug)

  if (!toSignupOptions) {
    return null
  }

  return (
    <Link
      className={className}
      onClick={onClick}
      search={{ redirect }}
      {...toSignupOptions}
    >
      Create now
    </Link>
  )
}
